import { useMutation, useQueryClient } from "@tanstack/react-query";

import { UnitCategory, UnitCategoryPayload, UnitCategoryRead } from "@types";
import {
  createUnitCategory,
  deleteUnitCategory,
  getAllUnitCategories,
  updateUnitCategory,
  uploadUnitCategoryVideo,
} from "@api";
import { useInfiniteList } from "@hooks";

export const UNIT_CATEGORY_QK = ["unit-categories"] as const;

export function useAllUnitCategories() {
  return useInfiniteList<UnitCategoryRead>({
    queryKey: UNIT_CATEGORY_QK,
    fetchFn: async (page) => {
      const res = await getAllUnitCategories(page);
      return res.data;
    },
  });
}
export function useCreateUnitCategory() {
  const queryClient = useQueryClient();

  const { mutateAsync: triggerCreateUnitCategory, isPending: loading } =
    useMutation<UnitCategoryRead, Error, UnitCategory>({
      mutationFn: (data) => createUnitCategory(data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: UNIT_CATEGORY_QK });
      },
    });

  return { triggerCreateUnitCategory, loading };
}

export function useUpdateUnitCategory() {
  const queryClient = useQueryClient();

  type UpdateArgs = { id: string; payload: UnitCategoryPayload };

  const { mutateAsync: triggerUpdateUnitCategory, isPending: loading } =
    useMutation<UnitCategoryRead, Error, UpdateArgs>({
      mutationFn: ({ id, payload }) => updateUnitCategory(id, payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: UNIT_CATEGORY_QK });
      },
    });

  return { triggerUpdateUnitCategory, loading };
}

export function useRemoveUnitCategoryById() {
  const queryClient = useQueryClient();

  const { mutateAsync: removeUnitCategory, isPending: loading } = useMutation<
    UnitCategoryRead,
    Error,
    string
  >({
    mutationFn: deleteUnitCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: UNIT_CATEGORY_QK });
    },
    onError: () => {
      console.error("Error deleting the unit category");
    },
  });

  return { removeUnitCategory, loading };
}

export function useUploadUnitCategoryVideo() {
  const queryClient = useQueryClient();

  type UploadArgs = { id: string; file: File };

  const { mutateAsync: triggerUploadVideo, isPending: loading } = useMutation<
    UnitCategoryRead,
    Error,
    UploadArgs
  >({
    mutationFn: ({ id, file }) => uploadUnitCategoryVideo(id, file),
    onSuccess: (updatedCategory) => {
      queryClient.setQueryData<UnitCategoryRead[]>(
        UNIT_CATEGORY_QK,
        (old = []) =>
          old.map((cat) =>
            cat.id === updatedCategory.id ? updatedCategory : cat,
          ),
      );

      queryClient.invalidateQueries({ queryKey: [UNIT_CATEGORY_QK] });
    },
  });

  return { triggerUploadVideo, loading };
}
